// SALESAXIS 2.0 — REAL ESTATE EDITION — Scoring Engine
import { SAXIS_RE_ITEMS, SAXIS_RE_DIMS } from "./questionBank.js";

export const SAXIS_RE_BANDS = [
  { min:0,  max:40, label:"Developing", level:"developing", color:"#dc2626", bg:"#fef2f2", border:"#fecaca", icon:"🔴" },
  { min:40, max:60, label:"Emerging",   level:"emerging",   color:"#d97706", bg:"#fffbeb", border:"#fde68a", icon:"🟡" },
  { min:60, max:80, label:"Strong",     level:"strong",     color:"#059669", bg:"#f0fdf4", border:"#86efac", icon:"🟢" },
  { min:80, max:101,label:"Exceptional",level:"exceptional",color:"#1d4ed8", bg:"#eff6ff", border:"#bfdbfe", icon:"🔵" },
];

// Role-fit archetypes based on top trait + role orientation lean.
const ARCHETYPES = {
  "RES_listing": { name:"The Closer", desc:"Results-driven and proactive, energised by actively pursuing and closing deals rather than waiting for them to come to you." },
  "RES_buyer":   { name:"The Advocate", desc:"Results-driven but in service of the client's outcome — relentlessly focused on getting your buyer the right home on the right terms." },
  "PER_listing": { name:"The Hunter", desc:"Tenacious and proactive, comfortable with the grind of prospecting and unwilling to let rejection slow you down." },
  "PER_buyer":   { name:"The Steady Guide", desc:"Tenacious in a quieter register — patiently sees a buyer through a long, uncertain search without losing momentum." },
  "EMP_listing": { name:"The Persuasive Partner", desc:"Empathetic but assertive — reads a seller's emotional stakes while still driving the listing and negotiation forward." },
  "EMP_buyer":   { name:"The Trusted Guide", desc:"Deeply attuned to client emotion, thrives on walking a buyer through one of the biggest decisions of their life." },
  "SYS_listing": { name:"The Strategist", desc:"Systematic and proactive — builds a personal process for consistently generating and converting listing opportunities." },
  "SYS_buyer":   { name:"The Organiser", desc:"Systematic in service of the buyer's journey — keeps a complex, multi-step search meticulously on track." },
  "FLX_listing": { name:"The Adaptive Dealmaker", desc:"Flexible and proactive — comfortable reshaping a strategy mid-negotiation to keep a listing moving toward close." },
  "FLX_buyer":   { name:"The Responsive Partner", desc:"Flexible in service of the buyer — adjusts easily as a client's needs and preferences evolve through their search." },
};

export function scoreSalesAxisRE(answers) {
  const dimScores = { SYS:0, PER:0, RES:0, FLX:0, EMP:0 };
  const dimCounts = { SYS:0, PER:0, RES:0, FLX:0, EMP:0 };
  let listingScore = 0, buyerScore = 0, roleCount = 0;

  SAXIS_RE_ITEMS.forEach((item, i) => {
    const raw = answers[i];
    if (raw === null || raw === undefined) return;
    if (item.dim === "ROLE") {
      if (item.lean === "listing") listingScore += Number(raw);
      else buyerScore += Number(raw);
      roleCount++;
    } else {
      dimScores[item.dim] += Number(raw);
      dimCounts[item.dim]++;
    }
  });

  const dimResults = {};
  Object.keys(SAXIS_RE_DIMS).forEach(dim => {
    const raw = dimScores[dim];
    const max = dimCounts[dim] * 4;
    const pct = max > 0 ? Math.round((raw / max) * 100) : 0;
    const band = SAXIS_RE_BANDS.find(b => pct >= b.min && pct < b.max) || SAXIS_RE_BANDS[0];
    dimResults[dim] = { raw, max, pct, band };
  });

  const overallPct = Math.round(
    Object.values(dimResults).reduce((s,d) => s+d.pct, 0) / Object.keys(dimResults).length
  );
  const overallBand = SAXIS_RE_BANDS.find(b => overallPct >= b.min && overallPct < b.max) || SAXIS_RE_BANDS[0];

  // Role-fit: -100 (pure buyer's agent) to +100 (pure listing agent), 0 = balanced/hybrid
  const listingMax = 4 * 4, buyerMax = 4 * 4; // 4 items each side, 0-4 pts per item
  const listingPct = Math.round((listingScore / listingMax) * 100);
  const buyerPct = Math.round((buyerScore / buyerMax) * 100);
  const roleFitIndex = listingPct - buyerPct; // -100 to +100
  const roleFitLabel =
    roleFitIndex >= 25 ? "Listing Agent Lean" :
    roleFitIndex <= -25 ? "Buyer's Agent Lean" :
    "Hybrid / Dual-Capable";

  // Top trait for archetype naming
  const topDim = Object.entries(dimResults).sort((a,b) => b[1].pct - a[1].pct)[0][0];
  const archKey = `${topDim}_${roleFitIndex >= 0 ? "listing" : "buyer"}`;
  const archetype = ARCHETYPES[archKey] || { name:"The All-Rounder", desc:"A balanced profile across trait and role dimensions, without one dominant lean." };

  const sortedDims = Object.entries(dimResults).sort((a,b) => b[1].pct - a[1].pct);
  const topTraits = sortedDims.slice(0,2).map(([id]) => id);
  const growthTraits = sortedDims.slice(-2).map(([id]) => id);

  return {
    dimResults, overallPct, band: overallBand,
    listingPct, buyerPct, roleFitIndex, roleFitLabel,
    archetype, topTraits, growthTraits,
    scales: { saxis_re: overallPct },
    perfAvg: overallPct/10, potAvg: overallPct/10,
    perfBox: overallPct>=80?3:overallPct>=60?2:1,
    potBox: overallPct>=80?3:overallPct>=60?2:1,
  };
}
